<template>
  <div class="scenario-modelling">
    <sm-select-modal
      v-if="!mode"
      :map="mapgl"
      :base-controller="BaseController"
      :editor-controller="EditorController"
    />
    <div class="public-transport-layout">
      <r-main-panel
        v-if="mode"
        :title="panelHeaderTitle"
        :subheader="model.name"
        :icon="panelHeaderIcon"
      >
        <sm-panel :map="mapgl" :state="editorState" :mode="mode" />
      </r-main-panel>
      <div id="scenario-modelling-map" class="scenario-modelling-map">
        <div v-if="overlay" class="scenario-modelling-overlay" />
        <div v-if="mode" class="scenario-modelling-controls">
          <el-button
            class="r-button"
            size="mini"
            type="primary"
            @click="backToModeSelect"
            :disabled="layersLoading"
          >
            <i class="el-icon-arrow-left" />
            К списку инструментов
          </el-button>
        </div>
        <div v-if="layersLoading" class="sm-loader">
          <i class="el-icon-loading" />
          Загрузка данных...
        </div>
        <div class="scenario-modelling-map-actions">
          <map-tools
            :map="mapgl"
            :is-traffic-counts="true"
            @change-prop="changeProp"
            :map-bearing="mapBearing"
            :is3d="is3d"
          />
          <sm-legend
            v-if="mode"
            :is-legend-active="isLegendActive"
            @toggle-legend="toggleLegend"
          />
        </div>
        <sm-timemachine
          v-if="mode && timeData && calcData && calcData.length"
        />
      </div>
    </div>
  </div>
</template>

<script>
import debounce from "lodash.debounce";
import cloneDeep from "lodash.clonedeep";
import SmSelectModal from "./components/sm-select-modal";
import SmPanel from "./components/sm-panel";
import SmLegend from "./components/sm-legend";
import SmTimemachine from "./components/sm-timemachine";
import MapTools from "../map/components/map-tools/map-tools";
import { initMap } from "./core";
import { initialEditorState } from "./configs";

export default {
  components: {
    SmSelectModal,
    SmPanel,
    SmLegend,
    SmTimemachine,
    MapTools
  },
  data() {
    return {
      mapgl: null,
      draw: null,
      mapLoading: null,
      mapBearing: null,
      is3d: false,
      BaseController: null,
      EditorController: null,
      isLegendActive: true,
      editorState: cloneDeep(initialEditorState)
    };
  },
  watch: {
    "$store.state.scenarioModelling.mode": function() {
      this.$nextTick(() => this.mapgl.resize());
    },
    "$store.state.theme": function(val, prev) {
      this.mapgl.setLayoutProperty(prev, "visibility", "none");
      this.mapgl.setLayoutProperty(val, "visibility", "visible");
    },
    "editorState.mode": function() {
      this.$nextTick(() => this.mapgl.resize({ noRequest: true }));
    },
    "$store.state.scenarioModelling.activeCalc": function() {
      this.BaseController.changeCalcPropHandler("calc");
    },
    "$store.state.scenarioModelling.diffCalc": function() {
      this.BaseController.changeCalcPropHandler("calc");
    },
    "$store.state.scenarioModelling.activeTimeValue": function() {
      this.BaseController.changeCalcPropHandler();
    },
    "$store.state.scenarioModelling.activeField": function() {
      this.BaseController.changeCalcPropHandler("field");
    },
    "$store.state.scenarioModelling.diagramWidth": debounce(function() {
      this.BaseController.updateDiagramLayer();
    }, 400),
    "$store.state.scenarioModelling.maxType": function() {
      this.BaseController.updateDiagramLayer();
    },
    "$store.state.scenarioModelling.manualMax": function() {
      this.BaseController.updateDiagramLayer();
    },
    "$store.state.scenarioModelling.showLabels": function(value) {
      this.BaseController.toggleLabels(value);
    }
  },
  computed: {
    mode() {
      return this.$store.state.scenarioModelling.mode;
    },
    model() {
      return this.$store.state.scenarioModelling.model;
    },
    overlay() {
      return this.$store.state.scenarioModelling.overlay;
    },
    layersLoading() {
      return this.$store.state.scenarioModelling.loadingLayers.length > 0;
    },
    timeData() {
      return this.$store.state.scenarioModelling.timeData;
    },
    calcData() {
      return this.$store.state.scenarioModelling.calcData;
    },
    panelHeaderTitle() {
      return "Сценарии и моделирование";
    },
    panelHeaderIcon() {
      return "modelling";
    }
  },
  methods: {
    saveMapParams() {
      const zoom = this.mapgl.getZoom();
      const center = this.mapgl.getCenter();

      localStorage.setItem("ptMapCenter", JSON.stringify(center));
      localStorage.setItem("ptMapZoom", JSON.stringify(zoom));
    },
    changeProp({ field, value }) {
      this[field] = value;
    },
    toggleLegend() {
      this.isLegendActive = !this.isLegendActive;
    },
    getObjectsLimit() {
      return 10000;
    },
    backToModeSelect() {
      if (this.layersLoading) return;
      // const { ids } = this.$store.state.scenarioModelling.model;
      // this.BaseController.toggleOff(ids);
      // this.EditorController.toggleEditorOff();

      this.$store.commit("SET_SM_STATE_PROP", {
        name: "mode",
        value: null
      });
      this.$store.commit("CHANGE_SM_CALC_PROP", {
        field: "activeCalc",
        value: null
      });
      this.$store.commit("CHANGE_SM_CALC_PROP", {
        field: "activeTimeValue",
        value: null
      });
      this.$store.commit("CHANGE_SM_CALC_PROP", {
        field: "activeField",
        value: null
      });
      this.$store.commit("CHANGE_SM_CALC_PROP", {
        field: "timeData",
        value: null
      });
      this.$store.commit("CHANGE_SM_CALC_PROP", {
        field: "diffCalc",
        value: null
      });
      this.$store.commit("CHANGE_SM_CALC_PROP", {
        field: "calcMode",
        value: "one"
      });

      this.BaseController.clearDiagramLayer();
    }
  },
  mounted() {
    initMap(this);
  },
  beforeDestroy() {
    window.removeEventListener("beforeunload", this.saveMapParams);
    this.$store.commit("CLEAR_SM_MODEL");
    this.$store.commit("SET_SM_STATE_PROP", { name: "mode", value: null });
    this.$store.commit("SET_SM_STATE_PROP", {
      name: "view",
      value: "model-list"
    });
    this.backToModeSelect();
    this.saveMapParams();
  }
};
</script>

<style lang="scss">
.scenario-modelling {
  height: 100%;
  width: 100%;
  display: flex;
  position: relative;
  background-color: var(--bg_surface) !important;

  &-layout {
    width: 100%;
    height: 100%;
    display: flex;
  }

  &-map {
    height: 100%;
    width: 100%;
  }

  &-overlay {
    position: absolute;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.5);
    z-index: 1000;
  }

  &-controls {
    position: absolute;
    left: 16px;
    top: 16px;
    z-index: 100;
    display: flex;
  }

  &-map-actions {
    position: absolute;
    right: 16px;
    top: 16px;
    z-index: 100;
  }

  // toggle off mapbox focus and controls
  .mapboxgl-canvas {
    &:focus {
      outline: none;
    }
  }
  .mapboxgl-ctrl-top-right {
    display: none;
  }
}

.sm-loader {
  position: absolute;
  top: 16px;
  left: calc(50%);
  transform: translateX(-50%);
  font-size: 12px;
  line-height: 18px;
  padding: 10px 20px;
  border-radius: var(--border-radius);
  border: 1px solid transparent;
  border-color: var(--dividers_low_contrast) !important;
  background-color: var(--bg_panel_primary) !important;
  display: flex;
  align-items: center;
  color: var(--accent_primary) !important;
  z-index: 100;

  i {
    font-size: 18px;
    line-height: 18px;
    margin-right: 5px;
  }
}
</style>
